/* FOM · gaveta lateral — investigacao, contrato, publicacao e trilha.

   O mapa continua sendo a tela. Isto e o que fica ao lado dele quando se quer
   ir mais fundo em um no ou no mapa inteiro.

   A trilha (historico) vive aqui e nao tem formulario nenhum: ela SO CRESCE.
   Nao ha editar, nao ha apagar, nao ha limpar. Isso nao e uma limitacao da
   tela — e a invariante I-5, e a tela seria o lugar errado para contorna-la. */
import type { Api } from '../core/api.ts';
import type { FomNode, HistoryEntry, MemberRole } from '../core/types.ts';
import { el, botao, limpar, vazio, dataCurta } from './dom.ts';
import { montarInvestigacao } from './investigacao.ts';
import { montarContrato } from './contrato.ts';
import { montarPublicacao } from './publicacao.ts';
import { montarOcultos } from './ocultos.ts';
import { montarBrain } from './brain.ts';

export type Aba = 'brain' | 'investigacao' | 'contrato' | 'publicacao' | 'ocultos' | 'trilha';

/* ordem por CONCEITO: contexto do trabalho, governanca, inteligencia.
   O separador visual vem do CSS (data-grupo). */
const ABAS: { id: Aba; rotulo: string; grupo: 'contexto' | 'governanca' | 'inteligencia' }[] = [
  { id: 'investigacao', rotulo: 'Investigacao', grupo: 'contexto' },
  { id: 'contrato', rotulo: 'Contrato', grupo: 'contexto' },
  { id: 'publicacao', rotulo: 'Publicacao', grupo: 'governanca' },
  { id: 'trilha', rotulo: 'Trilha', grupo: 'governanca' },
  { id: 'ocultos', rotulo: 'Ocultos', grupo: 'governanca' },
  { id: 'brain', rotulo: 'Brain', grupo: 'inteligencia' },
];

export type GavetaOpcoes = {
  api: Api;
  org: string;
  mapa: string;
  papel: MemberRole;
  aoAlterarNo: () => Promise<void>;
  aoErro: (erro: unknown) => void;
};

export function montarGaveta(host: HTMLElement, o: GavetaOpcoes) {
  host.className = 'gaveta';
  host.hidden = true;

  let ativa: Aba = 'investigacao';
  let no: FomNode | null = null;

  const barra = el('nav', 'gaveta-abas');
  const conteudo = el('div', 'gaveta-conteudo');
  const corpoBr = el('div', 'gaveta-painel');
  const corpoInv = el('div', 'gaveta-painel');
  const corpoCt = el('div', 'gaveta-painel');
  const corpoPub = el('div', 'gaveta-painel');
  const corpoOc = el('div', 'gaveta-painel');
  const corpoTr = el('div', 'gaveta-painel');

  const fechar = botao('×', () => { host.hidden = true; }, 'gaveta-fechar');
  fechar.title = 'fechar';
  fechar.setAttribute('aria-label', 'fechar gaveta');

  const botoes = new Map<Aba, HTMLButtonElement>();
  for (const a of ABAS) {
    const b = botao(a.rotulo, () => { void ir(a.id); }, 'gaveta-aba');
    b.dataset.aba = a.id;
    b.dataset.grupo = a.grupo;
    botoes.set(a.id, b);
    barra.appendChild(b);
  }
  barra.appendChild(fechar);

  conteudo.appendChild(corpoBr);
  conteudo.appendChild(corpoInv);
  conteudo.appendChild(corpoCt);
  conteudo.appendChild(corpoPub);
  conteudo.appendChild(corpoOc);
  conteudo.appendChild(corpoTr);
  host.appendChild(barra);
  host.appendChild(conteudo);

  const br = montarBrain(corpoBr, {
    api: o.api, org: o.org, mapa: o.mapa, papel: o.papel,
    aoAlterarDominio: o.aoAlterarNo, aoErro: o.aoErro,
  });
  const inv = montarInvestigacao(corpoInv, {
    api: o.api, org: o.org, mapa: o.mapa,
    aoAlterarNo: o.aoAlterarNo, aoErro: o.aoErro,
  });
  const ct = montarContrato(corpoCt, {
    api: o.api, org: o.org, mapa: o.mapa, aoErro: o.aoErro,
  });
  const pub = montarPublicacao(corpoPub, {
    api: o.api, org: o.org, mapa: o.mapa, papel: o.papel, aoErro: o.aoErro,
  });
  const oc = montarOcultos(corpoOc, {
    api: o.api, org: o.org, mapa: o.mapa,
    aoRestaurar: o.aoAlterarNo, aoErro: o.aoErro,
  });

  function pintarAbas(): void {
    for (const [id, b] of botoes) b.classList.toggle('gaveta-aba-ativa', id === ativa);
    corpoBr.hidden = ativa !== 'brain';
    corpoInv.hidden = ativa !== 'investigacao';
    corpoCt.hidden = ativa !== 'contrato';
    corpoPub.hidden = ativa !== 'publicacao';
    corpoOc.hidden = ativa !== 'ocultos';
    corpoTr.hidden = ativa !== 'trilha';
  }

  async function renderTrilha(): Promise<void> {
    limpar(corpoTr);
    corpoTr.appendChild(el('h3', 'gaveta-titulo', 'Trilha'));
    corpoTr.appendChild(el('p', 'gaveta-nota',
      'Tudo o que aconteceu neste mapa, em ordem. A trilha so cresce: nao ha '
      + 'editar, nao ha apagar. Cada linha diz se quem executou foi uma pessoa '
      + 'ou um agente — e uma acao de agente sempre teve uma pessoa autorizando.'));
    try {
      const entradas: HistoryEntry[] = await o.api.historico(o.org, o.mapa, 100);
      if (!entradas.length) {
        corpoTr.appendChild(vazio('Nenhuma mutacao registrada.',
          'A importacao da BASE nao conta como mutacao de usuario.'));
        return;
      }
      const ul = el('ul', 'lista-trilha');
      for (const h of entradas) {
        const porAgente = h.actor_type === 'agent';
        const li = el('li', 'trilha-item trilha-' + (porAgente ? 'agent' : 'human'));
        li.appendChild(el('span', 'trilha-data', dataCurta(h.at)));
        li.appendChild(el('span', 'trilha-acao', h.action));
        /* Quem agiu, sempre explicito. Numa acao de agente a pessoa que
           autorizou continua visivel: a maquina executou, mas alguem decidiu. */
        const autor = el('span', 'trilha-autor');
        if (porAgente) {
          autor.appendChild(el('span', 'selo-agente-mini', h.agent ?? 'agente'));
          autor.appendChild(el('span', 'trilha-por', ' por pessoa'));
          autor.title = 'executado pelo agente ' + (h.agent ?? '?')
            + ', autorizado por uma pessoa';
        } else {
          autor.textContent = 'pessoa';
        }
        li.appendChild(autor);
        ul.appendChild(li);
      }
      corpoTr.appendChild(ul);
    } catch (err) {
      o.aoErro(err);
    }
  }

  async function pintarConteudo(): Promise<void> {
    if (ativa === 'brain') await br.mostrar();
    else if (ativa === 'investigacao') await inv.mostrar(no);
    else if (ativa === 'contrato') await ct.mostrar(no);
    else if (ativa === 'publicacao') await pub.mostrar();
    else if (ativa === 'ocultos') await oc.mostrar();
    else await renderTrilha();
  }

  async function ir(destino: Aba): Promise<void> {
    ativa = destino;
    host.hidden = false;
    pintarAbas();
    await pintarConteudo();
  }

  pintarAbas();

  return {
    /* Abre a gaveta numa aba. Sempre recarrega do servidor: a gaveta nao e
       cache de tela, e o que ela mostra tem de ser o que o banco diz. */
    async abrir(destino: Aba, selecionado: FomNode | null): Promise<void> {
      no = selecionado;
      await ir(destino);
    },
    /* Transicao central do produto: uma lacuna do mapa vira uma pergunta. */
    async investigarLacuna(selecionado: FomNode, lacuna: string): Promise<void> {
      no = selecionado;
      ativa = 'investigacao';
      host.hidden = false;
      pintarAbas();
      await inv.investigarLacuna(selecionado, lacuna);
    },
    /* O no selecionado mudou no mapa: se a gaveta esta aberta numa aba que
       depende do no, ela acompanha. */
    async acompanhar(selecionado: FomNode | null): Promise<void> {
      no = selecionado;
      if (host.hidden) return;
      if (ativa === 'investigacao' || ativa === 'contrato') await pintarConteudo();
    },
    fechar(): void { host.hidden = true; },
    get aberta(): boolean { return !host.hidden; },
    get abaAtiva(): Aba { return ativa; },
  };
}
